import { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../authConfig';
import { searchUsers } from '../graphService';
import '../styles/UserSearch.css';

export default function UserSearch({ value, onChange, placeholder }) {
  const { instance, accounts } = useMsal();
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange({ name: val, email: '', id: '' }); // keep parent in sync with typed text

    clearTimeout(debounceRef.current);
    if (val.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const tokenRes = await instance.acquireTokenSilent({
          ...graphRequest,
          account: accounts[0],
        });
        const users = await searchUsers(tokenRes.accessToken, val);
        setResults(users);
        setOpen(users.length > 0);
      } catch {
        setResults([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleSelect = (user) => {
    setQuery(user.displayName);
    onChange({ name: user.displayName, email: user.mail || '', id: user.id, department: user.department || '' });
    setOpen(false);
    setResults([]);
  };

  const handleClear = () => {
    setQuery('');
    onChange({ name: '', email: '', id: '' });
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="user-search" ref={containerRef}>
      <div className="user-search-input-wrap">
        <input
          type="text"
          className="user-search-input"
          value={query}
          onChange={handleInput}
          placeholder={placeholder || 'Search for Employee Owner'}
          autoComplete="off"
        />
        {query && (
          <button type="button" className="user-search-clear" onClick={handleClear} aria-label="Clear">
            ✕
          </button>
        )}
        {loading && <span className="user-search-spinner" />}
      </div>

      {open && (
        <ul className="user-search-dropdown">
          {results.map((user) => (
            <li key={user.id} onMouseDown={() => handleSelect(user)} className="user-search-option">
              <span className="user-search-name">{user.displayName}</span>
              {user.mail && <span className="user-search-email">{user.mail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
